<template>
  <section
    class="product-details"
    :style="{
      '--sticky-top': `calc(${productAttributesStickyTop}px + var(--header-height))`,
    }"
  >
    <h2 class="sr-only">{{ $t('projectOne.card.choose', [familyDetailsData.familyName]) }}</h2>
    <customization-product-image
      v-if="isCustomizationMode && isCompactView"
      class="top-[var(--header-height)] z-20"
      :class="stackedViewDrawerOpen ? 'fixed' : 'sticky'"
    />
    <div id="buying-zone" ref="buying-zone" class="buying-zone relative mb-6 z-10" data-pdp>
      <div
        class="grid grid-cols-12 mb-6 gap-x-6 px-0 max-w-screen-xxl m-auto md:pt-3 md:min-h-screen lg:pt-4 lg:px-2"
        :data-brand-name="product.brandNameFull"
      >
        <input id="product-name" type="hidden" :value="product.name" />
        <input id="product-code" type="hidden" :value="variant.code" />
        <input id="product-brand" type="hidden" :value="variant.brandNameFull" />

        <!-- Left column -->
        <div
          ref="leftColumn"
          class="col-span-12 lg:col-span-7 xl:col-span-8 transition-all"
          :style="{columnContainerHeight: !isCustomizationMode}"
        >
          <product-images-component
            v-if="!isUpdatingProductImages && !isCustomizationMode"
            :images="images"
            class="mb-4 md:mb-0 md:sticky md:top-[calc(var(--header-height)+1rem)]"
          />
          <customization-product-image v-if="isCustomizationMode && !isCompactView" class="top-24 sticky" />
        </div>

        <!-- Right column -->
        <div :id="buyZoneId" ref="rightColumn" class="col-span-12 lg:col-span-5 xl:col-span-4" data-buying-zone>
          <pdl-callout v-if="hasBikeBeenSubmittedToRetailer && isCustomizationMode" kind="default">
            <template #content>
              <span>{{ $t('projectOne.config.summary.orderSubmitted') }}</span>
            </template>
          </pdl-callout>

          <div
            id="product-attributes"
            ref="productAttributes"
            :class="{'px-2 lg:px-0 lg:sticky': !isCustomizationMode}"
          >
            <product-attributes-container
              ref="attributesContainer"
              v-model="variantCode"
              :product="product"
              :render-stock-status="!options.kioskModeEnabledOnSite && options.eCommAvailable"
              :stock-message="stockMessage"
              :init-attribute-selection="initAttributeSelection"
              @attribute-selected="handleAttributeSelection"
              @color-changed="handleColorChange"
              @input="handleVariantChange"
            />

            <div v-if="isCustomizationMode" class="w-full px-2 lg:px-0 mb-1">
              <pdl-button block qaid="discard-customization-button" @click="openDiscardDialog">
                {{ $t('projectOne.buyingZone.customization.discard') }}
              </pdl-button>
            </div>
            <customize-product-button
              v-if="!product.isArchived"
              class="my-4"
              :project-one-url="projectOneUrl"
              :is-project-one-configurator-active="options.isProjectOneConfiguratorActive"
              :custom-configurator-type="product.customConfiguratorType"
              :custom-product-starting-point="product.customProductStartingPoint"
              :site-locale="options.encodedContextPath"
              :options="options"
            />

            <product-summary v-if="!isCustomizationMode" :product="product" class="mb-3" />

            <product-family-details-component
              :family-details-data="familyDetailsData"
              :product="product"
              :variant-code="variantCode"
              :has-30-day-price-display="true"
            />
            <pdp-build-a-bundle-cta
              v-if="showElectraAccessoryFinderCTA"
              class="cursor-pointer"
              :product="product"
              :variant-code="variantCode"
            />

            <pdp-pre-order v-if="hasFutureStock" :variant="variant" />

            <add-to-cart-container
              id="add-to-cart-container"
              :product="product"
              :is-purchasable="isPurchasable"
              :variant-code="variantCode"
              :options="options"
              :message="cartSuccessMessage"
              :add-to-cart-button-caption="addToCartButtonCaption"
              :stock-message="stockMessage"
              :track-event-data="trackEventData"
              :is-locally-stock-available="isLocallyStockVariantAvailable"
              @add-to-cart="
                (quantity) => {
                  addToCart(variantCode, quantity);
                }
              "
            >
              <template v-if="isLocallyWidgetButtonEnabled" #locally-button>
                <locally-product-finder
                  :variant="variant"
                  :variant-code="variantCode"
                  :product="product"
                  :options="options"
                  :show-locally-widget="showLocallyWidget"
                  @change-locally-widget="updateShowLocallyWidget"
                />
              </template>
            </add-to-cart-container>

            <div v-if="isCustomizationMode && !options.kioskModeEnabledOnSite" class="w-full px-2 lg:px-0">
              <div class="grid grid-cols-2 gap-2 mb-1">
                <customization-share-dropdown />
                <customization-save-button :watch-for-save="true" />
              </div>
            </div>
            <div v-if="product.buyingZoneCMSLink && product.buyingZoneCMSLink.length > 0">
              <buying-zone-cms-link
                v-for="(cmsLink, i) in product.buyingZoneCMSLink"
                :key="`cms-link-${i}`"
                :options="cmsLink"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CMS Consumer Message PDP -->
    <div class="max-w-screen-2xl px-2 mx-auto mb-6">
      <slot name="consumer-messages"></slot>
    </div>
  </section>
</template>

<script>
import {mapState, mapGetters, mapActions, mapMutations} from 'vuex';
import CustomizeProductButton from '@/components/containers/buy-zone/CustomizeProductButton.vue';
import AddToCartContainer from '@/components/containers/buy-zone/b2c/AddToCartContainer.vue';
import BuyingZoneCmsLink from '@/components/containers/buy-zone/BuyingZoneCmsLink.vue';
import LocallyProductFinder from '@/components/containers/buy-zone/LocallyProductFinder.vue';
import ProductAttributesContainer from '@/components/containers/buy-zone/b2c/ProductAttributesContainer.vue';
import PdpBuildABundleCta from '@/components/containers/pdp/PdpBuildABundleCta.vue';
import PdpPreOrder from '@/components/buyingzone/PdpPreOrder.vue';
import {ProductDetails} from '@/components/containers/pdp/utils/product-details.js';
import {loadVariantPricing} from '@/components/containers/pdp/utils/product-pricing.js';
import {AddToCart} from '@/components/containers/pdp/utils/add-to-cart';
import {InitProductAttributes} from '@/components/containers/pdp/utils/init-product-attributes';
import trackProduct from '@/mixins/tracking/product/track-product.js';
import Variant from '@/utils/variant-helpers';
import ProductFamilyDetailsComponent from '@/familyDetailsPage/components/ProductFamilyDetailsComponent.vue';
import {PdlButton} from '@pedal/pdl-button';
import {PdlCallout} from '@pedal/pdl-callout';
import ProductImagesComponent from '@/components/containers/buy-zone/ProductImagesComponent.vue';
import CustomizationProductImage from '@/components/containers/buy-zone/customization/CustomizationProductImage.vue';
import CustomizationSaveButton from '@/components/containers/buy-zone/customization/CustomizationSaveButton.vue';
import {Routes} from '@/constants/family-details.js';
import CustomizationShareDropdown from '@/components/containers/buy-zone/customization/CustomizationShareDropdown.vue';
import ProductSummary from '@/components/containers/buy-zone/ProductSummary.vue';
import {useFunctionalityToggle} from '@/composables/functionality-toggle.js';
import {FunctionalityToggle} from '@/constants/functionality-toggle.js';
import {usePrepareProductImages} from '@/composables/product-images.js';

export default {
  name: 'ProductDetails',
  components: {
    BuyingZoneCmsLink,
    AddToCartContainer,
    CustomizeProductButton,
    ProductAttributesContainer,
    LocallyProductFinder,
    PdpBuildABundleCta,
    PdpPreOrder,
    ProductFamilyDetailsComponent,
    ProductImagesComponent,
    PdlButton,
    PdlCallout,
    ProductSummary,
    CustomizationProductImage,
    CustomizationSaveButton,
    CustomizationShareDropdown,
  },
  mixins: [ProductDetails, InitProductAttributes, AddToCart, trackProduct],

  setup() {
    const isLocallyButtonEnabled = useFunctionalityToggle(
      FunctionalityToggle.LOCALLY_FIND_IN_STORE_BTN_TOGGLE
    ).isToggleActive;
    const isLocallyButtonEnabledArchived = useFunctionalityToggle(
      FunctionalityToggle.LOCALLY_ARCHIVED_PRODUCT_LOCATOR_TOGGLE
    ).isToggleActive;
    return {isLocallyButtonEnabled, isLocallyButtonEnabledArchived};
  },
  data() {
    return {
      componentPrefix: 'product-details',
      imageColumnContainerHeight: 0,
      productAttributesStickyTop: 0,
    };
  },

  computed: {
    ...mapState('productDetails', [
      'electraAccessoryFinderToggle',
      'pdpFunctionsMapping',
      'selectedColor',
      'selectedAttribute',
      'isCustomizationMode',
      'isUpdatingProductImages',
      'images',
    ]),
    ...mapState('backend', ['currencyIso', 'isElectraBrand']),
    ...mapState('familyDetails', ['selectedModelId', 'familyDetailsData', 'buyZoneId']),
    ...mapState('user', ['selectedRetailer']),
    ...mapState('savedLists', ['buyingZoneEntries']),
    ...mapState('global', ['headerHeight', 'isCompactView']),
    ...mapGetters('bikeCustomization', ['hasBikeBeenSubmittedToRetailer']),
    ...mapState('bikeCustomization', ['isCustomizationDrawerOpen', 'isDialogOpen']),

    stackedViewDrawerOpen() {
      return this.isCustomizationDrawerOpen && this.isCustomizationMode && this.isCompactView;
    },
    columnContainerHeight() {
      return {...(this.imageColumnContainerHeight && {height: `${this.imageColumnContainerHeight}px`})};
    },
    showElectraAccessoryFinderCTA() {
      return (
        this.variantCode &&
        this.isBike &&
        this.electraAccessoryFinderToggle &&
        this.pdpFunctionsMapping.ElectraAccessoryFinder &&
        this.isElectraBrand
      );
    },
    hasFutureStock() {
      return Boolean(this.variant?.futureStock);
    },
    isLocallyWidgetButtonEnabled() {
      if (this.options.kioskModeEnabledOnSite || this.isCustomizationMode) return false;
      return this.product.isArchived ? this.isLocallyButtonEnabledArchived : this.isLocallyButtonEnabled;
    },
  },

  watch: {
    variantCode(code) {
      const productCode = code || this.product.code;
      this.setVariantCode(productCode);
      usePrepareProductImages(this.product, productCode);

      const mechanicNotes = Variant(productCode, this.product).get()?.mechanicsNotes;
      this.setMechanicNotes(mechanicNotes);

      loadVariantPricing(code);

      // Updates trackEventData size variants
      let variantData = {variantCode: code, productSize: this.productSizeText(code)};
      this.setTrackData(variantData);

      window._fbq('track', 'ViewContent', {
        content_ids: [code],
        contents: [this.product.code],
        content_type: 'product',
      });

      if (this.$route?.name === Routes.FAMILY_DETAILS_PAGE) {
        this.$router.replace({
          name: Routes.FAMILY_DETAILS_PAGE,
          params: {
            ...this.$route.params,
            sku: code,
          },
        });
      }
    },
    consumerPrice(value) {
      this.isConsumerPriceReady = true;
      this.setTrackData({productPrice: value.displayPriceValue.calculable});
    },
    selectedModelId() {
      this.setSelectedColor('');
      this.initAttributeSelection = {};
      this.initProductAttributes();
    },
    selectedRetailer: {
      handler(newVal, oldVal) {
        if (newVal?.name !== oldVal?.name) {
          this.fetchLocallyAvailableVariants();
        }
      },
      immediate: true,
    },
    stackedViewDrawerOpen(isOpenValue) {
      this.toggleBodyOverflow(isOpenValue);
    },
    isDialogOpen(value) {
      this.toggleBodyOverflow(value);
    },
  },

  mounted() {
    this.setIsECommAvailable(this.options.eCommAvailable);
    this.setIsLocallyActive(this.options.isLocallyActive);
  },

  methods: {
    ...mapActions('productDetails', ['fetchLocallyAvailableVariants']),
    ...mapMutations('productDetails', [
      'setIsECommAvailable',
      'setIsLocallyActive',
      'setMechanicNotes',
      'setSelectedColor',
      'setSelectedAttribute',
      'setVariantCode',
    ]),
    ...mapMutations('bikeCustomization', ['setCustomizeDiscardDialog']),
    ...mapMutations('powerReviews', ['setIsWindowPwrLoaded']),
    ...mapMutations('familyDetails', ['setModelChangeConfirmCallback']),

    galleryDOMReady() {
      this.getContainerHeight();
    },
    handleColorChange(color) {
      if (this.selectedColor !== color) {
        this.setSelectedColor(color);
      }
    },
    handleAttributeSelection(attribute) {
      if (this.selectedAttribute !== attribute) {
        this.setSelectedAttribute(attribute);
      }
    },
    handleVariantChange(code) {
      if (this.isCustomizationMode) {
        this.variantCode = null;
      } else {
        this.variantCode = code;
      }
    },
    getProductAttributesStickyTop() {
      const chatBotHeight = 98;
      const visibleHeight = window.innerHeight - this.headerHeight - chatBotHeight;
      const stickyHeight = visibleHeight - this.$refs.productAttributes?.offsetHeight;
      this.productAttributesStickyTop = stickyHeight < 0 ? stickyHeight : 0;
    },
    openDiscardDialog() {
      this.setModelChangeConfirmCallback(() => {
        this.$router.replace({
          name: Routes.FAMILY_DETAILS_PAGE,
          params: {
            ...this.$route.params,
          },
        });
      });
      this.setCustomizeDiscardDialog(true);
    },
    toggleBodyOverflow(isHidden) {
      if (isHidden) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
#product-attributes {
  @media (screen(lg)) {
    top: var(--sticky-top, var(--header-height));
  }
}
</style>
